import { todayPT } from "@homebound/activesupport";
import {
  BoundRadioGroupField,
  BoundSelectField,
  Button,
  FormLines,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useCallback, useMemo } from "react";
import { BidContractType, InternalEstimateType, Maybe, SaveBidPackageRequestInput } from "src/generated/graphqlTypes";
import {
  AddTradePartnerModal_BidPackageFragment,
  BidPackageDetailPage_TradeFragment,
  useAddTradePartnerModalQuery,
} from "src/routes/bids/components/AddTradePartnerModal.generated";
import { useSaveBidPackageRequestMutation } from "src/routes/bids/components/BiddersTab.generated";
import { useCreateBidContractMutation } from "src/routes/developments/procurement/components/CreateDevelopmentContractButton.generated";
import { queryResult } from "src/utils";
import { INTERNAL_ESTIMATE_ID } from "../plan-based-bid-package/PlanBasedBidPackagePage";

export function AddTradePartnerModal(props: { bidPackage: AddTradePartnerModal_BidPackageFragment }) {
  const { bidPackage } = props;
  const query = useAddTradePartnerModalQuery({ variables: { costCodes: bidPackage.costCodes.map((cc) => cc.id) } });
  return queryResult(query, ({ tradePartners }) => (
    <AddTradePartnerModalView trades={tradePartners} bidPackage={bidPackage} />
  ));
}

function AddTradePartnerModalView(props: {
  trades: BidPackageDetailPage_TradeFragment[];
  bidPackage: AddTradePartnerModal_BidPackageFragment;
}) {
  const { trades, bidPackage } = props;
  const showInternalEstimate = bidPackage.isPlanBasedV2 && !bidPackage.internalEstimateBidContract;
  const { closeModal } = useModal();
  const [saveBidPackageRequest] = useSaveBidPackageRequestMutation();
  const [createBidContract] = useCreateBidContractMutation();
  const formState = useFormState({
    config: formConfig,
    init: { onlyOnce: true, input: { bidPackageId: bidPackage.id } },
  });
  const matchingTrades = useMemo(
    () => [
      ...trades
        .filter((tp) => bidPackage.costCodes.every((bpcc) => tp.costCodes.map((cc) => cc.id).includes(bpcc.id)))
        .filter((tp) => !bidPackage.requests.map((r) => r.tradePartner.id).includes(tp.id)),
      ...(showInternalEstimate ? [{ id: INTERNAL_ESTIMATE_ID, name: "Homebound Internal Estimate" }] : []),
    ],
    [(bidPackage.costCodes, trades, bidPackage.requests, showInternalEstimate)],
  );
  const noNewTrades = useMemo(() => matchingTrades.isEmpty, [matchingTrades.isEmpty]);

  const saveAndExit = useCallback(async () => {
    // If the user selected the internal estimate, create an internal estimate bid contract
    if (formState.tradePartnerId.value === INTERNAL_ESTIMATE_ID) {
      await createBidContract({
        variables: {
          input: {
            isInternalEstimate: true,
            internalEstimateBidPackageId: bidPackage.id,
            startDate: todayPT(),
            type: BidContractType.UserCreatedGroupBased,
            internalEstimateType: formState.internalEstimateType.value,
          },
        },
      });
    } else {
      await saveBidPackageRequest({ variables: { input: { ...formState.value } } });
    }
    closeModal();
  }, [closeModal, formState, saveBidPackageRequest, createBidContract, bidPackage.id]);

  return (
    <div data-testid="addTradeModal">
      <ModalHeader>Add Trade</ModalHeader>
      <ModalBody>
        {noNewTrades ? (
          <span>No New Trades to Add</span>
        ) : (
          <FormLines>
            <BoundSelectField
              field={formState.tradePartnerId}
              label="Company Name"
              options={matchingTrades}
              onSelect={(v) => {
                if (v === INTERNAL_ESTIMATE_ID) {
                  formState.internalEstimateType.set(InternalEstimateType.Temporary);
                }
                formState.tradePartnerId.set(v);
              }}
            />
            <Observer>
              {() =>
                formState.tradePartnerId.value === INTERNAL_ESTIMATE_ID ? (
                  <BoundRadioGroupField
                    label="Estimate Type"
                    options={[
                      { label: " Temporary", value: InternalEstimateType.Temporary },
                      { label: " Lot Determined", value: InternalEstimateType.LotDetermined },
                    ]}
                    field={formState.internalEstimateType}
                  />
                ) : null
              }
            </Observer>
          </FormLines>
        )}
      </ModalBody>
      <ModalFooter>
        <>
          <Button label="Cancel" onClick={closeModal} variant="quaternary" />
          <Button label="Save" onClick={saveAndExit} disabled={noNewTrades} />
        </>
      </ModalFooter>
    </div>
  );
}

type BidTradesFormValue = Omit<SaveBidPackageRequestInput, "primaryContactId" | "secondaryContactId"> & {
  internalEstimateType: Maybe<InternalEstimateType>;
};
const formConfig: ObjectConfig<BidTradesFormValue> = {
  bidPackageId: { type: "value" },
  tradePartnerId: { type: "value" },
  internalEstimateType: { type: "value" },
};
